import React, { useContext, useMemo, useState } from "react";
import "./certifications.css";
import Page from "../../Utilities/Page";
import DataContext from "../../../DataContext";
import FilterBar from "../../Utilities/FilterBar";
import Card from "../../Utilities/Card";
import { issuerOf, monogramOf, yearOf } from "../../../Utils/certIssuer";

/* the tile and the issuer line are both derived from the title, because
   /certifications stores only { date, title } */
const certTile = (title) => {
  const issuer = issuerOf(title);

  return (
    <div className={issuer.light ? "tile light fcc" : "tile fcc"}>
      {issuer.logo ? <img src={issuer.logo} alt={issuer.name} /> : <b>{monogramOf(title)}</b>}
    </div>
  )
}

const certMeta = (data) => {
  const issuer = issuerOf(data.title || "").name
    || data.issuer || data.organization || data.type || "";
  const time = data.date || data.placement || "";

  return [issuer, time].filter(Boolean).join(" · ");
}

const Certifications = () => {
  const { certs } = useContext(DataContext);
  const [search, setSearch] = useState("");
  const [issuer, setIssuer] = useState("");
  const [year, setYear] = useState("");

  const records = useMemo(() => certs || [], [certs]);

  const issuers = useMemo(() => {
    const names = records
      .map((cert) => issuerOf(cert.data.title || "").name)
      .filter(Boolean);

    return [...new Set(names)].sort();
  }, [records]);

  const years = useMemo(() => {
    const found = records
      .map((cert) => yearOf(cert.data.date || cert.data.placement || ""))
      .filter(Boolean);

    return [...new Set(found)].sort((a, b) => b.localeCompare(a));
  }, [records]);

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();

    return records.filter((cert) => {
      const title = cert.data.title || "";
      const date = cert.data.date || cert.data.placement || "";

      if (query !== "" && !title.toLowerCase().includes(query)) return false;
      if (issuer !== "" && issuerOf(title).name !== issuer) return false;
      if (year !== "" && yearOf(date) !== year) return false;

      return true;
    });
  }, [records, search, issuer, year]);

  /* the shell stays mounted while the records load, so the navigation bar and
     the footer never disappear between routes */
  const loading = !certs || certs.length === 0;

  return (
    <Page cls="certifications fcs" title="Certifications | Chai Cheng Xun">
      <div className="page-wrap">
        <div className="cmd">
          <span className="prompt">$</span> cat ~/certs
        </div>
        <div className="d-title">Certifications</div>
        <div className="d-text intro">
          The certifications I have earned along the way, each one a checkpoint
          in offensive security and penetration testing.
        </div>
        {loading ? (
          <div className="d-text loading">Loading...</div>
        ) : (
        <>
        <FilterBar
          placeholder="Search certifications..."
          search={search}
          setSearch={setSearch}
          filters={[
            {
              label: "Issuer",
              options: issuers,
              value: issuer,
              onChange: setIssuer,
            },
            {
              label: "Year",
              options: years,
              value: year,
              onChange: setYear,
            },
          ]}
          shown={shown.length}
          total={records.length}
        />
        {shown.length === 0 ? (
          <div className="d-text empty">Nothing matches that filter.</div>
        ) : (
          <div className="card-grid">
            {shown.map((cert) => (
              <Card
                key={cert.id}
                to={cert.data.link || ""}
                icon={certTile(cert.data.title || "")}
                title={cert.data.title || "Untitled Certification"}
                meta={certMeta(cert.data)}
              />
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </Page>
  );
};

export default Certifications;
